/**
 * Quick Setup Guide Component
 * 
 * Provides step-by-step guidance for setting up integrations
 */

import React, { useState } from 'react'
import { 
  CheckCircle, 
  Circle, 
  ArrowRight, 
  ExternalLink, 
  Copy,
  AlertCircle,
  BookOpen
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { integrationUtils } from '@/features/integrations/utils/integrationUtils'
import { IntegrationType } from '@/types/integrations'

interface QuickSetupGuideProps {
  onCreateIntegration: (type: IntegrationType) => void
}

const setupSteps = {
  jobber_crm: [
    {
      title: 'Access Jobber Developer Console',
      description: 'Log into your Jobber account and navigate to the Developer section',
      action: 'Visit Jobber Developer Console',
      url: 'https://developer.getjobber.com',
    },
    {
      title: 'Create OAuth Application',
      description: 'Create a new OAuth application for the FixMyBrick admin dashboard',
      action: 'Create Application',
    },
    {
      title: 'Copy Credentials',
      description: 'Copy the Client ID and Client Secret from your application',
      action: 'Copy to clipboard',
    },
    {
      title: 'Configure Integration',
      description: 'Add the credentials to your integration configuration',
      action: 'Configure Now',
    },
  ],
  google_my_business: [
    {
      title: 'Google Cloud Console',
      description: 'Access Google Cloud Console and enable the My Business API',
      action: 'Open Google Cloud Console',
      url: 'https://console.cloud.google.com',
    },
    {
      title: 'Create OAuth Client',
      description: 'Create OAuth 2.0 credentials for web application',
      action: 'Create Client',
    },
    {
      title: 'Configure Redirect URIs',
      description: 'Add the admin dashboard callback URL to authorized redirects',
      action: 'Configure URIs',
    },
    {
      title: 'Connect Account',
      description: 'Authorize the integration to access your GMB account',
      action: 'Connect Now',
    },
  ],
  google_analytics: [
    {
      title: 'Google Analytics Property',
      description: 'Ensure you have admin access to the GA4 property',
      action: 'Check Access',
      url: 'https://analytics.google.com',
    },
    {
      title: 'Enable Analytics API',
      description: 'Enable the Google Analytics Reporting API in Google Cloud Console',
      action: 'Enable API',
    },
    {
      title: 'Get Property ID',
      description: 'Find your GA4 property ID from the Admin section',
      action: 'Find Property ID',
    },
    {
      title: 'Connect Integration',
      description: 'Authorize the integration with your Google account',
      action: 'Connect Now',
    },
  ],
  google_search_console: [
    {
      title: 'Google Search Console Access',
      description: 'Ensure you have access to Google Search Console',
      action: 'Check Access',
      url: 'https://search.google.com/search-console',
    },
    {
      title: 'Verify Website',
      description: 'Verify your website ownership in Search Console',
      action: 'Verify Site',
    },
    {
      title: 'Enable Search Console API',
      description: 'Enable the Google Search Console API in Google Cloud Console',
      action: 'Enable API',
    },
    {
      title: 'Connect Integration',
      description: 'Authorize the integration with your Google account',
      action: 'Connect Now',
    },
  ],
  facebook_pixel: [
    {
      title: 'Facebook Business Manager',
      description: 'Access your Facebook Business Manager account',
      action: 'Open Business Manager',
      url: 'https://business.facebook.com',
    },
    {
      title: 'Create App',
      description: 'Create a new app in Facebook for Developers',
      action: 'Create App',
    },
    {
      title: 'Add Marketing API',
      description: 'Add the Marketing API product to your app',
      action: 'Add Product',
    },
    {
      title: 'Get Access Token',
      description: 'Generate a long-lived access token for the integration',
      action: 'Generate Token',
    },
  ],
  social_media: [
    {
      title: 'Social Platform Access',
      description: 'Ensure you have admin access to all social media accounts',
      action: 'Verify Access',
    },
    {
      title: 'API Keys and Tokens',
      description: 'Gather API keys and access tokens for each platform',
      action: 'Collect Credentials',
    },
    {
      title: 'Configure Webhooks',
      description: 'Set up webhooks for real-time social media updates',
      action: 'Setup Webhooks',
    },
    {
      title: 'Test Integration',
      description: 'Verify the social media integration works correctly',
      action: 'Test Now',
    },
  ],
  wordpress: [
    {
      title: 'WordPress Admin Access',
      description: 'Ensure you have administrator access to the WordPress site',
      action: 'Check Access',
    },
    {
      title: 'Enable REST API',
      description: 'Verify that the WordPress REST API is enabled and accessible',
      action: 'Test API',
    },
    {
      title: 'Create Application Password',
      description: 'Generate an application password for API authentication',
      action: 'Create Password',
    },
    {
      title: 'Test Connection',
      description: 'Verify the connection before starting migration',
      action: 'Test Now',
    },
  ],
}

const prerequisites = {
  jobber_crm: [
    'Active Jobber account with API access',
    'Admin permissions in Jobber',
    'Franchise region information',
  ],
  google_my_business: [
    'Google My Business account',
    'Verified business locations',
    'Google Cloud Console access',
  ],
  google_analytics: [
    'Google Analytics 4 property',
    'Admin access to GA4 property',
    'Google Cloud Console access',
  ],
  google_search_console: [
    'Google Search Console access',
    'Verified website ownership',
    'Google Cloud Console access',
  ],
  facebook_pixel: [
    'Facebook Business Manager account',
    'Facebook for Developers access',
    'Pixel ID from your Facebook account',
  ],
  social_media: [
    'Admin access to social media accounts',
    'API credentials for each platform',
    'Content approval permissions',
  ],
  wordpress: [
    'WordPress admin access',
    'WordPress version 5.6 or higher',
    'REST API enabled',
  ],
}

export function QuickSetupGuide({ onCreateIntegration }: QuickSetupGuideProps) {
  const [activeGuide, setActiveGuide] = useState<IntegrationType>('jobber_crm')
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({})

  const toggleStep = (stepIndex: number) => {
    const key = `${activeGuide}-${stepIndex}`
    setCompletedSteps(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const renderGuide = (type: IntegrationType) => {
    const steps = setupSteps[type] || []
    const prereqs = prerequisites[type] || []

    return (
      <div className="space-y-6">
        {/* Prerequisites */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Prerequisites
            </CardTitle>
            <CardDescription>
              Make sure you have the following before starting the setup
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {prereqs.map((prereq: string, index: number) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{prereq}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Setup Steps */}
        <Card>
          <CardHeader>
            <CardTitle>Setup Steps</CardTitle>
            <CardDescription>
              Follow these steps to configure your {integrationUtils.typeLabels[type]} integration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {steps.map((step: any, index: number) => {
                const isCompleted = completedSteps[`${type}-${index}`]
                return (
                  <div
                    key={index}
                    className={`p-4 border rounded-lg transition-colors ${
                      isCompleted 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => toggleStep(index)}
                        className="mt-1 flex-shrink-0"
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className={`font-medium ${
                            isCompleted ? 'text-green-900' : 'text-gray-900'
                          }`}>
                            Step {index + 1}: {step.title}
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {index + 1} of {steps.length}
                          </Badge>
                        </div>
                        <p className={`text-sm mt-1 ${
                          isCompleted ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {step.description}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          {step.url && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(step.url, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              {step.action}
                            </Button>
                          )}
                          {!step.url && step.action.includes('clipboard') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard('example-credentials')}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              {step.action}
                            </Button>
                          )}
                          {index === steps.length - 1 && (
                            <Button
                              size="sm"
                              onClick={() => onCreateIntegration(type)}
                            >
                              {step.action}
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Additional Resources */}
        <Alert>
          <BookOpen className="h-4 w-4" />
          <AlertDescription>
            Need help? Check out our{' '}
            <a href="#" className="underline">detailed documentation</a>{' '}
            or{' '}
            <a href="#" className="underline">contact support</a>{' '}
            for assistance with the {integrationUtils.typeLabels[type]} integration.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Quick Setup Guide</h2>
        <p className="text-muted-foreground mt-1">
          Step-by-step instructions for setting up your integrations
        </p>
      </div>

      <Tabs value={activeGuide} onValueChange={(value) => setActiveGuide(value as IntegrationType)}>
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5">
          <TabsTrigger value="jobber_crm">Jobber CRM</TabsTrigger>
          <TabsTrigger value="google_my_business">GMB</TabsTrigger>
          <TabsTrigger value="google_analytics">Analytics</TabsTrigger>
          <TabsTrigger value="facebook_pixel">Facebook</TabsTrigger>
          <TabsTrigger value="wordpress">WordPress</TabsTrigger>
        </TabsList>

        <TabsContent value="jobber_crm">
          {renderGuide('jobber_crm')}
        </TabsContent>
        
        <TabsContent value="google_my_business">
          {renderGuide('google_my_business')}
        </TabsContent>
        
        <TabsContent value="google_analytics">
          {renderGuide('google_analytics')}
        </TabsContent>
        
        <TabsContent value="facebook_pixel">
          {renderGuide('facebook_pixel')}
        </TabsContent>
        
        <TabsContent value="wordpress">
          {renderGuide('wordpress')}
        </TabsContent>
      </Tabs>
    </div>
  )
}